import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"

import config from "config"
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "layouts/notifications/service"

const NotificationContext = createContext(null)

const getToken = () => sessionStorage.getItem("token") || localStorage.getItem("token")

function NotificationProvider({ children }) {
  const [items, setItems] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const wsRef = useRef(null)
  const pingRef = useRef(null)
  const reconnectRef = useRef(null)

  const token = getToken()

  const load = useCallback(async () => {
    if (!token) return
    try {
      const [page, count] = await Promise.all([fetchNotifications(0, 20), fetchUnreadCount()])
      setItems(page?.content ?? [])
      setUnreadCount(count)
    } catch (error) {
      console.error("Error cargando notificaciones:", error)
    }
  }, [token])

  const markRead = useCallback(async (id) => {
    const target = items.find((n) => n.id === id)
    if (!target || target.read) return
    const ok = await markNotificationRead(id)
    if (ok) {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
      setUnreadCount((prev) => Math.max(0, prev - 1))
    }
  }, [items])

  const markAllRead = useCallback(async () => {
    if (unreadCount === 0) return
    try {
      await markAllNotificationsRead()
      setItems((prev) => prev.map((n) => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error("Error marcando todas como leídas:", error)
    }
  }, [unreadCount])

  // Carga inicial por REST
  useEffect(() => {
    load()
  }, [load])

  // Tiempo real por WebSocket (mismo patrón que ingresos)
  useEffect(() => {
    if (!token) return undefined

    const wsProto = window.location.protocol === "https:" ? "wss" : "ws"
    const host = new URL(config.api.baseURL).host
    const clean = token.replace(/\s+/g, "")
    const wsUrl = `${wsProto}://${host}/ws/notifications?token=${encodeURIComponent(clean)}`

    let closedByEffect = false

    const connect = () => {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        if (pingRef.current) clearInterval(pingRef.current)
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping")
        }, 25000)
      }

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data)
          if (payload?.type !== "notification" || !payload?.data?.id) return
          const dto = payload.data
          setItems((prev) => {
            if (prev.some((n) => n.id === dto.id)) return prev
            return [dto, ...prev]
          })
          if (!dto.read) setUnreadCount((prev) => prev + 1)
        } catch (error) {
          console.error("Error procesando notificación ws:", error)
        }
      }

      ws.onclose = (evt) => {
        if (pingRef.current) {
          clearInterval(pingRef.current)
          pingRef.current = null
        }
        if (!closedByEffect && (evt.code === 1006 || evt.code === 1011)) {
          reconnectRef.current = setTimeout(connect, 2000)
        }
      }
    }

    connect()

    return () => {
      closedByEffect = true
      if (pingRef.current) clearInterval(pingRef.current)
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      if (wsRef.current) wsRef.current.close()
    }
  }, [token])

  const value = useMemo(
    () => ({ items, unreadCount, load, markRead, markAllRead }),
    [items, unreadCount, load, markRead, markAllRead]
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useNotifications = () => {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    return { items: [], unreadCount: 0, load: () => {}, markRead: () => {}, markAllRead: () => {} }
  }
  return ctx
}

export default NotificationProvider
